import { call, put, takeEvery } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  UploadVaccination,
  FetchDriver,
  UploadInsuranceBeneficiary,
  UploadVehicleInsurance,
  UploadInsuranceDeclaration,
  UploadVehicleRegistration,
  UploadMVR,
  UploadForm,
  UploadBGC,
  UploadEIN,
  UploadInsuranceCoverage,
} from 'src/constants/actionTypes'
import { ApiService, callClientSecureApi } from './api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import { InsuranceBeneficiaryRelation } from 'src/constants/insuranceBeneficiary'
import { UploadFormResponse } from 'src/interfaces/collectDataForm'
import { captureSentryError } from 'src/utils/sentry'

interface UploadFormRequest {
  data: Array<{ id: string; value: any }>
  files: Array<{ id: string; value: File }>
  stepId: string
}

function* onUploadForm(action: AsyncAction<UploadFormRequest>) {
  const { data, stepId, files } = action.payload

  const formData = new FormData()

  if (files.length) {
    files.forEach((item) => {
      formData.append('files', item.value, item.id)
    })
  }

  formData.append(
    'body',
    JSON.stringify({
      data,
      stepId,
    }),
  )

  try {
    const { payload }: { payload: UploadFormResponse } = yield call(
      callClientSecureApi,
      Endpoints.UploadForm,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        body: formData,
        resetContentType: true,
      },
    )

    yield put(UploadForm.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadForm] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to upload form'

    yield put(UploadForm.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadVehicleInsuranceRequest {
  stepId: string
  file: File
  policyDocument?: File
  type: string
  year?: string | number
  carMake?: string
  carModel?: string
  vin?: string | number
}

function* onUploadVehicleInsurance(action: AsyncAction<UploadVehicleInsuranceRequest>) {
  const { file, type, year, carMake, carModel, vin, stepId, policyDocument } = action.payload

  try {
    const formData = new FormData()

    formData.append('file', file)
    formData.append('type', type)
    formData.append('stepId', stepId)

    if (policyDocument) {
      formData.append('policyDocument', policyDocument)
    }

    if (year) {
      formData.append('year', String(year))
    }

    if (carMake) {
      formData.append('carMake', carMake)
    }

    if (carModel) {
      formData.append('carModel', carModel)
    }

    if (vin) {
      formData.append('vin', String(vin))
    }

    formData.forEach((value, key) => console.log(value, key))

    yield call(callClientSecureApi, Endpoints.UploadVehicleInsurance, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: formData,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(UploadVehicleInsurance.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadVehicleInsurance] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to upload vehicle insurance'

    yield put(UploadVehicleInsurance.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadInsuranceDeclarationRequest {
  files: File[]
  stepId: string
}

function* onUploadInsuranceDeclaration(action: AsyncAction<UploadInsuranceDeclarationRequest>) {
  const { files, stepId } = action.payload

  try {
    const formData = new FormData()

    files.forEach((file) => {
      formData.append('file', file)
    })
    formData.append('stepId', stepId)

    formData.forEach((value, key) => console.log(value, key))

    yield call(callClientSecureApi, Endpoints.UploadInsuranceDeclaration, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: formData,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(UploadInsuranceDeclaration.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadInsuranceDeclaration] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to upload insurance declaration'

    yield put(UploadInsuranceDeclaration.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadVehicleRegistrationRequest {
  file: File
  stepId: string
}

function* onUploadVehicleRegistration(action: AsyncAction<UploadVehicleRegistrationRequest>) {
  const { file, stepId } = action.payload

  try {
    const formData = new FormData()

    formData.append('file', file)
    formData.append('stepId', stepId)

    formData.forEach((value, key) => console.log(value, key))

    yield call(callClientSecureApi, Endpoints.UploadVehicleRegistration, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: formData,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(UploadVehicleRegistration.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadVehicleRegistration] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to upload vehicle registration'

    yield put(UploadVehicleRegistration.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadVaccinationRequest {
  brand?: string
  date?: string
  isVaccinated: boolean
  stepId: string
}

function* onUploadVaccination(action: AsyncAction<UploadVaccinationRequest>) {
  const { brand, date, isVaccinated, stepId } = action.payload

  try {
    const body: UploadVaccinationRequest = {
      isVaccinated,
      stepId,
    }

    if (isVaccinated && brand && date) {
      body.brand = brand
      body.date = date
    }

    yield call(callClientSecureApi, Endpoints.UploadVaccination, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body,
    })

    yield put(UploadVaccination.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadVaccination] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to upload vaccination'

    yield put(UploadVaccination.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadInsuranceBeneficiaryRequest {
  firstName: string
  lastName: string
  relation: InsuranceBeneficiaryRelation
  stepId: string
}

function* onUploadInsuranceBeneficiary(action: AsyncAction<UploadInsuranceBeneficiaryRequest>) {
  const { firstName, lastName, relation, stepId } = action.payload

  try {
    yield call(callClientSecureApi, Endpoints.UploadInsuranceBeneficiary, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        firstName,
        lastName,
        relation,
        stepId,
      },
    })

    yield put(UploadInsuranceBeneficiary.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadInsuranceBeneficiary] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to upload insurance beneficiary'

    yield put(UploadInsuranceBeneficiary.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadMVRRequest {
  file?: File
  stepId: string
}

function* onUploadMVR(action: AsyncAction<UploadMVRRequest>) {
  const { file, stepId } = action.payload

  try {
    const formData = new FormData()

    formData.append('stepId', stepId)

    if (file) {
      formData.append('file', file)
    }

    formData.forEach((value, key) => console.log(value, key))

    yield call(callClientSecureApi, Endpoints.UploadMVR, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: formData,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(UploadMVR.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadMVR] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to upload mvr'

    yield put(UploadMVR.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadBGCRequest {
  files: Array<{ file: File; name: string }>
  stepId: string
}

function* onUploadBGC(action: AsyncAction<UploadBGCRequest>) {
  const { files, stepId } = action.payload

  try {
    const formData = new FormData()

    formData.append('stepId', stepId)

    if (files.length) {
      files.forEach((item) => {
        formData.append('files', item.file, item.name)
      })
    }

    formData.forEach((value, key) => console.log(value, key))

    yield call(callClientSecureApi, Endpoints.UploadBGC, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: formData,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(UploadBGC.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadBGC] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to upload bgc'

    yield put(UploadBGC.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadEINRequest {
  stepId: string
  file?: File
  ein?: string | number
  businessName?: string
  streetAddress?: string
  city?: string
  state?: string
  zipCode?: string | number
  isBusinessEntityFlow?: boolean
}

function* onUploadEIN(action: AsyncAction<UploadEINRequest>) {
  const {
    file,
    ein,
    zipCode,
    stepId,
    businessName,
    streetAddress,
    city,
    state,
    isBusinessEntityFlow,
  } = action.payload

  try {
    const formData = new FormData()

    formData.append('stepId', stepId)
    formData.append('isBusinessEntityFlow', JSON.stringify(isBusinessEntityFlow))

    if (file) {
      formData.append('file', file)
    }

    if (ein) {
      formData.append('ein', String(ein))
    }

    if (zipCode) {
      formData.append('zipCode', String(zipCode))
    }

    if (businessName) {
      formData.append('businessName', businessName)
    }

    if (streetAddress) {
      formData.append('streetAddress', streetAddress)
    }

    if (city) {
      formData.append('city', city)
    }

    if (state) {
      formData.append('state', state)
    }

    formData.forEach((value, key) => console.log(value, key))

    yield call(callClientSecureApi, Endpoints.UploadEIN, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: formData,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(UploadEIN.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadEIN] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to upload ein'

    yield put(UploadEIN.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface UploadInsuranceCoverageRequest {
  stepId: string
  files?: File[]
  optOut: boolean
}

function* onUploadInsuranceCoverage(action: AsyncAction<UploadInsuranceCoverageRequest>) {
  const { files, optOut, stepId } = action.payload

  try {
    const formData = new FormData()

    formData.append('optOut', JSON.stringify(optOut))
    formData.append('stepId', stepId)

    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append('file', file)
      })
    }

    formData.forEach((value, key) => console.log(value, key))

    yield call(callClientSecureApi, Endpoints.UploadInsuranceCoverage, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: formData,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(UploadInsuranceCoverage.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadInsuranceCoverage] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to upload insurance'

    yield put(UploadInsuranceCoverage.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* documentsSaga() {
  yield takeEvery(UploadForm.type.REQUEST, onUploadForm)
  yield takeEvery(UploadVehicleInsurance.type.REQUEST, onUploadVehicleInsurance)
  yield takeEvery(UploadInsuranceDeclaration.type.REQUEST, onUploadInsuranceDeclaration)
  yield takeEvery(UploadVehicleRegistration.type.REQUEST, onUploadVehicleRegistration)
  yield takeEvery(UploadVaccination.type.REQUEST, onUploadVaccination)
  yield takeEvery(UploadInsuranceBeneficiary.type.REQUEST, onUploadInsuranceBeneficiary)
  yield takeEvery(UploadMVR.type.REQUEST, onUploadMVR)
  yield takeEvery(UploadBGC.type.REQUEST, onUploadBGC)
  yield takeEvery(UploadEIN.type.REQUEST, onUploadEIN)
  yield takeEvery(UploadInsuranceCoverage.type.REQUEST, onUploadInsuranceCoverage)
}
